/**
 * Team Inbox — Send-Picker PURE logic (TEAMINBOX_SEND_PICKER_BUILD_SPEC).
 *
 * No React, no Firebase — every branch is unit-testable. Mirrors the mockup's
 * reference JS: {{token}}/{{n}} blank detection, the 3-level name hierarchy
 * (saved contact → WhatsApp profile name → graceful generic), live preview
 * fill, and the required-blank send gate (the #132000 guard, client side).
 */
import type { CannedReply } from './types';

/* ───────────────────────── name hierarchy (§5) ───────────────────────── */

export interface NameContext {
  /** First name from the saved CRM contact (source #1). */
  contactFirst?: string | null;
  /** The sender's WhatsApp profile display name (source #2). */
  waProfileName?: string | null;
}

export type NameSource = 'contact' | 'wa_profile' | 'none';

/** PURE — which name source applies, and the resolved first name (may be ''). */
export function resolveName(ctx: NameContext): { source: NameSource; name: string } {
  const c = String(ctx.contactFirst || '').trim();
  if (c) return { source: 'contact', name: firstWord(c) };
  const w = String(ctx.waProfileName || '').trim();
  if (w) return { source: 'wa_profile', name: firstWord(w) };
  return { source: 'none', name: '' };
}

function firstWord(s: string): string {
  return String(s).trim().split(/\s+/)[0] || '';
}

/** PURE — the editable-input hint under a name blank, per the resolved source. */
export function nameHint(source: NameSource, kind: 'canned' | 'tpl'): string {
  if (source === 'contact') return '✓ Auto-filled from your saved contact — edit if needed.';
  if (source === 'wa_profile')
    return '✓ Pulled from their WhatsApp profile name (not in your contacts yet) — edit if needed.';
  return kind === 'canned'
    ? 'No name available anywhere — sends as just "Namaste 🙏". Type one to personalise.'
    : 'No name available anywhere — sends as "there". Type one to personalise.';
}

/** Graceful generic fallback when no name is on file (§5 row 3). */
export const NAME_FALLBACK_CANNED = ''; // canned greetings already carry "ji"/"जी"
export const NAME_FALLBACK_TPL = 'there'; // English templates read "Hi {{1}},"

/* ───────────────────────── blank detection ───────────────────────── */

/** Is this canned {{token}} a NAME blank (optional, auto-resolved)? */
export function isNameTok(tok: string): boolean {
  return tok === 'first' || /name/i.test(tok);
}
/** Is this template variable LABEL a name blank? */
export function isNameLabel(label: string): boolean {
  return /first|name/i.test(label);
}

/** PURE — distinct {{token}} names across a canned message's EN + HI bodies. */
export function cannedVars(en: string, hi: string): string[] {
  const seen: string[] = [];
  const re = /\{\{(\w+)\}\}/g;
  let m: RegExpExecArray | null;
  const body = `${en || ''} ${hi || ''}`;
  while ((m = re.exec(body)) !== null) if (!seen.includes(m[1])) seen.push(m[1]);
  return seen;
}

/** PURE — "first_name" → "First Name" for a friendly blank label. */
export function humanize(t: string): string {
  return String(t)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** PURE — collapse the double-space / dangling-punctuation left when a name slot is empty. */
export function tidy(s: string): string {
  return String(s)
    .replace(/ {2,}/g, ' ')
    .replace(/\s+([,.;])/g, '$1')
    .trim();
}

/* ───────────────────────── preview fill ───────────────────────── */

export interface PreviewSegment {
  /** 'text' = literal · 'filled' = a supplied/name value · 'blank' = an unfilled ‹label›. */
  kind: 'text' | 'filled' | 'blank';
  text: string;
}

/**
 * PURE — render a CANNED body (en or hi) into segments for the WhatsApp-bubble
 * preview. A name {{token}} with no value falls back to the resolved name, then
 * to the graceful generic (empty → the slot collapses cleanly). A non-name
 * blank with no value shows a ‹Label› hint.
 */
export function previewCannedSegments(
  body: string,
  values: Record<string, string>,
  name: string,
): PreviewSegment[] {
  return splitFill(body, /\{\{(\w+)\}\}/g, (tok) => {
    let v = values[tok];
    if (isNameTok(tok)) {
      if (v == null || v === '') v = name || NAME_FALLBACK_CANNED;
      return v ? { kind: 'filled', text: v } : { kind: 'text', text: '' };
    }
    return v
      ? { kind: 'filled', text: v }
      : { kind: 'blank', text: `‹${humanize(tok)}›` };
  });
}

/**
 * PURE — render a TEMPLATE body ({{1}}/{{2}}…) into preview segments using the
 * ordered variable labels. Name blanks fall back to the resolved name then to
 * "there"; non-name blanks show ‹Label›.
 */
export function previewTplSegments(
  body: string,
  variables: string[],
  values: Record<string, string>,
  name: string,
): PreviewSegment[] {
  return splitFill(body, /\{\{(\d+)\}\}/g, (nStr) => {
    const n = Number(nStr);
    const label = variables[n - 1] || `{{${n}}}`;
    let v = values[`v${n}`];
    if (isNameLabel(label)) {
      if (v == null || v === '') v = name || NAME_FALLBACK_TPL;
      return v ? { kind: 'filled', text: v } : { kind: 'text', text: '' };
    }
    return v
      ? { kind: 'filled', text: v }
      : { kind: 'blank', text: `‹${label}›` };
  });
}

/** Render preview segments to a plain string (for the actual SEND body). */
export function segmentsToText(segs: PreviewSegment[]): string {
  return tidy(segs.map((s) => s.text).join(''));
}

function splitFill(
  body: string,
  re: RegExp,
  fill: (token: string) => PreviewSegment,
): PreviewSegment[] {
  const out: PreviewSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  re.lastIndex = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: body.slice(last, m.index) });
    out.push(fill(m[1]));
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ kind: 'text', text: body.slice(last) });
  return out;
}

/* ───────────────────────── send gate (the #132000 fix, client side) ─────────── */

/**
 * PURE — is a CANNED message ready to send? Every NON-name blank must be
 * filled (name blanks are optional → never block). Returns true when ready.
 */
export function cannedReady(vars: string[], values: Record<string, string>): boolean {
  return vars
    .filter((t) => !isNameTok(t))
    .every((t) => !!(values[t] && values[t].trim()));
}
/** PURE — count of unfilled NON-name canned blanks. */
export function cannedMissing(vars: string[], values: Record<string, string>): number {
  return vars.filter((t) => !isNameTok(t) && !(values[t] && values[t].trim())).length;
}

/** PURE — is a TEMPLATE ready? Every non-name {{n}} must be filled. */
export function tplReady(variables: string[], values: Record<string, string>): boolean {
  return variables.every(
    (lab, i) => isNameLabel(lab) || !!(values[`v${i + 1}`] && values[`v${i + 1}`].trim()),
  );
}
/** PURE — count of unfilled non-name template blanks. */
export function tplMissing(variables: string[], values: Record<string, string>): number {
  return variables.filter(
    (lab, i) => !isNameLabel(lab) && !(values[`v${i + 1}`] && values[`v${i + 1}`].trim()),
  ).length;
}

/**
 * PURE — build the ordered templateParams array a template send needs, filling
 * name slots from the resolved name / "there" and leaving others from `values`.
 * This is what guarantees the param COUNT == the template's {{n}} count (the
 * server #132000 guard then double-checks).
 */
export function buildTemplateParams(
  variables: string[],
  values: Record<string, string>,
  name: string,
): string[] {
  return variables.map((lab, i) => {
    const v = values[`v${i + 1}`];
    if (v != null && v !== '') return v;
    if (isNameLabel(lab)) return name || NAME_FALLBACK_TPL;
    return '';
  });
}

/* ───────────────────────── canned-store helpers ───────────────────────── */

/** A canned's display title (new `title`, else legacy `slash`). */
export function cannedTitle(c: CannedReply): string {
  return c.title || c.slash || c.id || 'Untitled';
}
/** A canned's English body (new `textEn`, else legacy `text`). */
export function cannedEn(c: CannedReply): string {
  return c.textEn ?? c.text ?? '';
}
/** A canned's Hindi body (empty when a legacy doc has none). */
export function cannedHi(c: CannedReply): string {
  return c.textHi ?? '';
}
/** A canned's id (new `id`, else its slash). */
export function cannedId(c: CannedReply): string {
  return c.id || c.slash || '';
}

/**
 * PURE — the canned messages a given staff member may SEE in the picker:
 * shared (visibility 'shared'/undefined) + their own personal ones.
 */
export function visibleCanned(all: CannedReply[], myStaffId: string | null): CannedReply[] {
  return all.filter(
    (c) => c.visibility !== 'me' || (!!myStaffId && c.ownerUid === myStaffId),
  );
}

/** PURE — group canned messages by category (preserving first-seen order). */
export function groupCannedByCategory(
  list: CannedReply[],
): { category: string; items: CannedReply[] }[] {
  const order: string[] = [];
  const map = new Map<string, CannedReply[]>();
  for (const c of list) {
    const cat = c.category || 'Other';
    if (!map.has(cat)) {
      map.set(cat, []);
      order.push(cat);
    }
    map.get(cat)!.push(c);
  }
  return order.map((category) => ({ category, items: map.get(category)! }));
}
